import { createRouter, createWebHistory, FundriseRouteConfig, Router } from 'vue-router';
import { isSignUpRoute, isValidSlug } from '@utils/routing';
import addTrackers from '@utils/trackers';
import { appBasePath } from '@constants';
import { getVanityUrlMetadata } from '@api/vanity';
import { handleAuthentication } from './auth-guard';
import homeRedirects from './redirects/home-redirects';
import investmentsRedirects from './redirects/investments-redirects';
import { pageViewed } from '@utils/page-viewed';
import { registerIntercom } from '@utils/intercom';
import { useAppStore } from '@stores/app';

let router: Router;

const routerPromise: Promise<Router> = new Promise((resolve, reject) => {
	function initializeRouter(routes: FundriseRouteConfig[]) {
		router = createRouter({
			history: createWebHistory(`${appBasePath}`),
			scrollBehavior(to, from, savedPosition) {
				if (savedPosition) {
					return savedPosition;
				} else {
					if (to.hash) {
						return {
							el: to.hash,
							behavior: 'smooth',
							top: 70
						};
					}

					return { left: 0, top: 0 };
				}
			},
			routes
		});

		router.beforeEach(async (to, from) => {
			const redirectArray = [homeRedirects, investmentsRedirects];
			const foundRedirect = redirectArray.find((entry) => to.path.match(entry.regExPaths) !== null);

			if (foundRedirect) {
				return { name: foundRedirect.routeName, replace: true };
			} else if (!to.matched.length) {
				const slug = to.path.substring(1);

				if (isValidSlug(slug)) {
					try {
						const marketingRedirect = await getVanityUrlMetadata(slug);
						return {
							query: {
								utm_source: marketingRedirect.utmSource,
								utm_medium: marketingRedirect.utmMedium,
								utm_campaign: marketingRedirect.utmCampaign,
								utm_content: marketingRedirect.utmContent
							},
							path: marketingRedirect.redirectUrl,
							replace: true
						};
					} catch {
						return '/404';
					}
				}

				return '/404';
			} else {
				return handleAuthentication(to, from);
			}
		});

		router.isReady().then(async () => {
			const appStore = useAppStore();

			if (!appStore.isMobileWebview) {
				if (import.meta.env.VITE_ENABLE_CONVERSION_PIXELS === 'true') {
					addTrackers();
				}
			}
		});

		router.beforeResolve((to) => {
			const appStore = useAppStore();
			appStore.getCurrentBanner(to.path);
		});

		router.afterEach(async (to) => {
			pageViewed(to);

			await registerIntercom(isSignUpRoute(to.path) ? 'SIGN_UP' : 'STANDARD');
		});

		resolve(router);
	}

	import('./routes')
		.then((module) => {
			initializeRouter(module.routes);
		})
		.catch(() => reject());
});

export default routerPromise;
